// ControlDetailPage — T2 (Object Detail + Tabs) layout.
// Displays a single control with tabbed sections.
// Route: /t/:slug/controls/:controlId (and sub-routes for tabs)

import { useState } from 'react'
import { useParams } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useApi } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import {
  controlsApi,
  controlLifecycleStateLabels,
  controlLifecycleStateColors,
} from '@/lib/api/controls'
import { queryKeys } from '@/lib/api/queryKeys'
import { ErrorState } from '@/components/shared/ErrorState'
import { FormSkeleton } from '@/components/shared/LoadingSkeleton'
import { DetailPageShell, type TabDef } from '@/components/shared/DetailPageShell'
import { Button } from '@/components/ui/button'
import { MoveControlDialog } from './MoveControlDialog'
import { User, Calendar, FolderOpen, MoveRight } from 'lucide-react'

const tabs: TabDef[] = [
  { id: 'detail', label: 'Detail', path: '' },
  { id: 'risks', label: 'Linked Risks', path: '/risks', countKey: 'linked_risk_count' },
  { id: 'obligations', label: 'Obligations', path: '/obligations', countKey: 'compliance_mapping_count' },
  { id: 'assessments', label: 'Assessments', path: '/assessments', overdueKey: 'has_overdue_assessments' },
  { id: 'kpis', label: 'KPIs', path: '/kpis' },
  { id: 'evidence', label: 'Evidence', path: '/evidence' },
  { id: 'history', label: 'Audit Log', path: '/history' },
]

export function ControlDetailPage() {
  const { slug } = useTenant()
  const { controlId } = useParams({ strict: false }) as { controlId: string }
  const api = useApi()
  const ctrlApi = controlsApi(api)
  const [moveOpen, setMoveOpen] = useState(false)

  const {
    data: control,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: queryKeys.controls.detail(slug, controlId),
    queryFn: () => ctrlApi.get(controlId),
  })

  if (isLoading) return <FormSkeleton />

  if (error) {
    return (
      <div className="p-6">
        <ErrorState
          message="Failed to load control"
          technicalDetail={error.message}
          onRetry={() => refetch()}
        />
      </div>
    )
  }

  if (!control) return null

  return (
    <DetailPageShell
      title={control.name}
      code={control.short_code || undefined}
      stateLabel={controlLifecycleStateLabels[control.lifecycle_state] ?? control.lifecycle_state}
      stateColorClass={controlLifecycleStateColors[control.lifecycle_state]}
      backUrl={`/t/${slug}/controls`}
      backLabel="Back to control list"
      tabs={tabs}
      basePath={`/t/${slug}/controls/${controlId}`}
      data={control as unknown as Record<string, unknown>}
      actions={
        <>
          <Button variant="outline" size="sm" onClick={() => setMoveOpen(true)}>
            <MoveRight className="mr-1.5 h-3.5 w-3.5" />
            Move to...
          </Button>
          <MoveControlDialog
            open={moveOpen}
            onClose={() => setMoveOpen(false)}
            controlId={controlId}
            currentCategoryId={control.category_id}
            currentCategoryName={control.category_name}
            onMoved={() => setMoveOpen(false)}
          />
        </>
      }
      metadata={
        <>
          <span className="flex items-center gap-1">
            <FolderOpen className="h-3.5 w-3.5" />
            {control.category_name}
          </span>
          {control.owner_display_name && (
            <span className="flex items-center gap-1">
              <User className="h-3.5 w-3.5" />
              {control.owner_display_name}
            </span>
          )}
          <span className="flex items-center gap-1">
            <Calendar className="h-3.5 w-3.5" />
            {new Date(control.updated_at).toLocaleDateString()}
          </span>
        </>
      }
    />
  )
}
